<template>
  <!-- ===================================================================
       APPLICATION MOBILE — écran de confirmation.
       Arbre distinct de celui du site : style.css peint les balises nues et
       rattraperait n'importe quel balisage neuf. Le site est servi inchangé
       par le <template v-else> plus bas.
       =================================================================== -->
  <div v-if="isNative" class="cf-page">

    <!-- 1. La confirmation. Seul moment de l'application où une réussite
         mérite d'occuper l'écran ; elle tient au-dessus de la ligne de
         flottaison avec la date, sans photo qui la repousse. -->
    <header class="cf-hero">
      <span class="cf-hero__mark" aria-hidden="true">
        <svg class="cf-hero__check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20 6 9 17l-5-5"/>
        </svg>
      </span>
      <h1 class="cf-hero__title">Réservation confirmée</h1>
      <p class="cf-hero__sub">Votre place est retenue. Vous la retrouverez à tout moment dans « Mes réservations ».</p>
    </header>

    <!-- 2. Ce qui a été réservé -->
    <section class="cf-card">
      <p class="cf-card__label">Votre sortie</p>

      <div class="cf-date">
        <span class="cf-date__label">{{ isStageSejour ? 'Période' : 'Rendez-vous' }}</span>
        <span class="cf-date__val">{{ isStageSejour ? 'Du' : 'Le' }} {{ formattedDates }}<template v-if="schedule"> {{ schedule }}</template></span>
      </div>

      <div class="cf-act">
        <img v-if="imageSrc" class="cf-act__img" :src="imageSrc" alt="" width="80" height="60" loading="lazy">
        <span class="cf-act__text">
          <span class="cf-act__sport">{{ cfOd.sport }}<template v-if="cfOd.type"> · {{ cfOd.type }}</template></span>
          <span class="cf-act__title" v-html="cfTitre"></span>
          <span class="cf-act__who">
            {{ quantity }} participant{{ Number(quantity) > 1 ? 's' : '' }}
            <template v-if="cfVariation"> · {{ cfVariation }}</template>
          </span>
        </span>
      </div>

      <a class="cf-map" :href="mapLink" target="_blank" rel="noopener">
        <span class="cf-map__pin" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor"><path d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z"/></svg>
        </span>
        <span class="cf-map__text">
          <span v-if="cfOd.clubName" class="cf-map__name">{{ cfOd.clubName }}</span>
          <span class="cf-map__addr">{{ cfAdresse }}</span>
        </span>
        <svg class="cf-map__go" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>
      </a>
    </section>

    <!-- 3. Le détail payé -->
    <section class="cf-card">
      <p class="cf-card__label">Détail du règlement</p>
      <div class="cf-recap">
        <div class="cf-recap__row">
          <span>{{ quantity }} × {{ cfOd.sport }}</span>
          <span>{{ cfEuro(price) }} €</span>
        </div>
        <div class="cf-recap__row" v-for="(eq, i) in equipmentQuantities" :key="i">
          <span>{{ eq.quantity }} × {{ eq.name || i }}</span>
          <span>{{ cfEuro(eq.price * eq.quantity) }} €</span>
        </div>
        <div class="cf-recap__row" v-if="cfAssurancePrise">
          <span>Assurance Cap Explorer <span class="cf-chip">Confirmé</span></span>
          <span>{{ cfEuro(chapkaInsurance.premium) }} €</span>
        </div>
        <div class="cf-recap__row cf-recap__row--total">
          <span>Total réglé</span>
          <span>{{ cfEuro(cfTotal) }} €</span>
        </div>
      </div>
    </section>

    <!-- 4. Statut de l'assurance. Deux états réels : le contrat peut être
         en cours de traitement chez l'assureur au moment du paiement. -->
    <section v-if="cfAssurancePrise" class="cf-card">
      <p class="cf-card__label">Assurance Chapka</p>
      <div class="cf-ins" :class="cfAssuranceOk ? 'is-ok' : 'is-wait'">
        <span class="cf-ins__icon" aria-hidden="true">
          <svg v-if="cfAssuranceOk" viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M12 1 3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-1.2 15L7 12.2l1.4-1.4 2.4 2.4 5-5L17.2 9l-6.4 7z"/></svg>
          <svg v-else viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm1 11h-2V6h2v7zm0 4h-2v-2h2v2z"/></svg>
        </span>
        <span class="cf-ins__text">
          <span class="cf-ins__title">{{ cfAssuranceOk ? 'Assurance Cap Explorer souscrite' : 'Assurance en cours de traitement' }}</span>
          <span class="cf-ins__desc">
            {{ cfAssuranceOk
              ? "Votre attestation d'assurance vous sera envoyée par e-mail."
              : "Votre souscription suit son cours : l'assureur vous contactera prochainement. Votre réservation, elle, est bien enregistrée." }}
          </span>
        </span>
      </div>
    </section>

    <!-- 5. Et maintenant ? Rien d'inventé ici : chaque point s'appuie sur une
         donnée réellement présente dans la réponse de l'offre. -->
    <section class="cf-card">
      <p class="cf-card__label">Et maintenant ?</p>
      <ol class="cf-next">
        <li class="cf-next__item">
          <span class="cf-next__num">1</span>
          <span class="cf-next__text">
            <span class="cf-next__head">Présentez-vous sur place</span>
            <span class="cf-next__sub">
              {{ isStageSejour ? 'Du' : 'Le' }} {{ formattedDates }}<template v-if="schedule"> {{ schedule }}</template>,
              {{ cfAdresse }}.
            </span>
          </span>
        </li>
        <li v-if="cfInclus.length" class="cf-next__item">
          <span class="cf-next__num">2</span>
          <span class="cf-next__text">
            <span class="cf-next__head">Fourni sur place</span>
            <span class="cf-next__sub">{{ cfInclus.join(', ') }}.</span>
          </span>
        </li>
        <li class="cf-next__item">
          <span class="cf-next__num">{{ cfInclus.length ? 3 : 2 }}</span>
          <span class="cf-next__text">
            <span class="cf-next__head">En cas d'empêchement</span>
            <span class="cf-next__sub">
              Annulez depuis « Mes réservations », ou
              <RouterLink :to="{ name: 'Contact' }" class="cf-link">écrivez-nous</RouterLink>.
            </span>
          </span>
        </li>
      </ol>
    </section>

    <!-- 6. Sorties : l'écran ne doit pas être un cul-de-sac. -->
    <div class="cf-exit">
      <RouterLink :to="{ name: 'MyOffers' }" class="cf-btn cf-btn--main">
        Voir mes réservations
        <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h13M12.5 5.5 19 12l-6.5 6.5"/></svg>
      </RouterLink>
      <RouterLink :to="{ name: 'HomePage' }" class="cf-btn cf-btn--ghost">Retour à l'accueil</RouterLink>
    </div>
  </div>

  <!-- ===================================================================
       SITE WEB — balisage d'origine, inchangé.
       =================================================================== -->
  <template v-else>
    <section class="hero3"
           :style="offerDetails.offerDetails.sport 
                   ? `background: url('${apiImg(`/api/gallery_img/banner_${offerDetails.offerDetails.sport}.png`)}') no-repeat center 50%/cover;`
                   : ''">
    </section>

    <div class="activity-details">
     <!-- Écran de réussite — application mobile uniquement -->
     <div class="native-success" v-if="isNative">
       <span class="native-success__mark" aria-hidden="true">
         <svg viewBox="0 0 52 52" class="native-success__icon">
           <circle cx="26" cy="26" r="23" fill="none" stroke="currentColor" stroke-width="2" opacity="0.3"></circle>
           <path d="M16 26.5 L23 33.5 L37 19" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"></path>
         </svg>
       </span>
       <h2 class="native-success__title">Réservation confirmée</h2>
       <p class="native-success__text">Merci pour votre réservation !</p>
     </div>

     <div class="activity-details-top">
         <h2>{{offerDetails.offerDetails.sport}} <br><span v-if="formattedDates.length > 0 && !isStageSejour">
{{ formattedDates }}
</span><span v-else>
{{ formattedDates }}
</span></h2>
        <h3 v-if="offerDetails.offerDetails.titleOffers" v-html="offerDetails.offerDetails.titleOffers"></h3>
        <h3 v-else v-html="offerDetails.presentation"></h3>
         <h2 style="text-align: right;"> {{ offerDetails.offerDetails.type }} <br>{{ offerDetails.offerDetails.finalCity }} ({{ offerDetails.offerDetails.finalPostcode }})</h2>
     </div>
     <div class="activity-details-description" style="display: grid; grid-template-columns: 1fr 1fr;">
         <p class="activity-location">{{ isStageSejour ? 'Du' : 'Le' }} {{ formattedDates }} {{ findScheduleByDate(dateSelected)}} : <br><a :href="'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(offerDetails.offerDetails.finalAddress + ' ' + offerDetails.offerDetails.finalCity + ' ' + offerDetails.offerDetails.finalPostcode)" target="_blank" style="text-decoration: underline;"> <i class="fa fa-map-marker"></i>
 {{ offerDetails.offerDetails.finalAddress }} {{ offerDetails.offerDetails.finalCity }} {{ offerDetails.offerDetails.finalPostcode }}
</a>
  <span v-html="offerDetails.offerDetails.details"></span></p>

<div class="image_activity"><img :src="imageSrc" />
</div>
</div>

    <div class="reservation-form" style="text-align: center;">
      <h3>CONFIRMATION DE RÉSERVATION</h3>
      <br>
        <div class="form-section options">
            <input type="text" class="btn quantity-input" value="Quantité" disabled style="width: auto;">
            <input type="number" class="btn quantity-input" :value="quantity" disabled>
            <input type="text" class="btn quantity-input" :value="price + ' €'" disabled style="width: auto;">

        </div>
        <div class="form-section options" v-for="(equipment, index) in equipmentQuantities" :key="index">
            <input type="text" class="btn quantity-input" :value="equipment.name" disabled style="width: auto;">
          <input type="number" class="btn quantity-input" :value="equipment.quantity" disabled>
          <input type="text" class="btn quantity-input" :value="equipment.price * equipment.quantity + '€'" disabled style="width: auto;">
        </div>

        <!-- Affichage de l'assurance Chapka si souscrite -->
        <div v-if="chapkaInsurance && chapkaInsurance.accepted" class="insurance-confirmation-recap">
          <div class="insurance-recap-label">
            <span class="shield-icon">🛡️</span>
            <span class="insurance-name">Assurance Cap Explorer</span>
            <span class="insurance-tag">Confirmé</span>
          </div>
          <div class="insurance-price">{{ chapkaInsurance.premium }} €</div>
        </div>

        
      </div>
      <br>
      <br>
      <div class="button-container-center">
      <div class="thank-you" style="text-align: center;">
        <p>MERCI POUR VOTRE RÉSERVATION !</p>
      </div>
      
      <!-- Message de confirmation assurance Chapka -->
      <div v-if="chapkaInsurance && chapkaInsurance.accepted" class="insurance-confirmation-message">
        <div v-if="chapkaSubscribed && chapkaSubscribed.success" class="insurance-success">
          <i class="fa fa-check-circle"></i>
          <div>
            <strong>Assurance Cap Explorer souscrite</strong>
            <p>Vous recevrez votre attestation d'assurance par email.</p>
          </div>
        </div>
        <div v-else-if="chapkaSubscribed && !chapkaSubscribed.success" class="insurance-warning">
          <i class="fa fa-exclamation-triangle"></i>
          <div>
            <strong>Assurance en cours de traitement</strong>
            <p>Votre demande d'assurance est en cours. Vous serez contacté prochainement.</p>
          </div>
        </div>
      </div>
      
      <br>
      <RouterLink to="/" class="login-button oc-btn oc-btn--primary oc-btn--block" style="background-color: #e74c3c;">Revenir à l'accueil</RouterLink>
    </div>
    </div>
  </template>

  </template>
  <script>
  import { ref, computed, watch } from 'vue';
  import { apiImg, isNative } from '@/api/base';

  export default {
    setup() {
      const formattedDates = ref(localStorage.getItem('formattedDates'));
      const quantity = ref(localStorage.getItem('quantity'));
      const raw = localStorage.getItem('offerDetails');
      const offerDetails = ref(raw ? JSON.parse(raw) : null);
      const equipmentQuantities = ref(JSON.parse(localStorage.getItem('equipmentQuantities') || '[]'));
      const variation = ref(JSON.parse(localStorage.getItem('variation') || 'null'));
      
      // Récupérer les données de l'assurance Chapka
      const chapkaRaw = localStorage.getItem('chapkaInsurance');
      const chapkaInsurance = ref(chapkaRaw ? JSON.parse(chapkaRaw) : null);
      const chapkaSubscribed = ref(JSON.parse(localStorage.getItem('chapkaSubscribed') || 'null'));

      watch(offerDetails, (newVal) => {
        if (newVal) {
          newVal
        }
      });
  


        const convertDateFormat = (date) => {
        if (!date) return null;
        const [year, month, day] = date.split("-");
        return `${day}/${month}/${year} 00:00`;
        };
        const convertDateFormatForce = (date) => {
          if (!date) return null;

          let day, month, year;

          if (date.includes("-")) {
            [year, month, day] = date.split("-");
          } else {
            const parts = date.split(" ");
            day = parts[0].padStart(2, "0");
            year = parts[2];
            const monthName = parts[1].toLowerCase();

            const monthsMap = {
              janvier: "01", février: "02", mars: "03", avril: "04",
              mai: "05", juin: "06", juillet: "07", août: "08",
              septembre: "09", octobre: "10", novembre: "11", décembre: "12"
            };

            month = monthsMap[monthName] || "00";
          }

          return `${day}/${month}/${year} 00:00`;
        };

        const isStageSejour = computed(() => {
          const result = offerDetails.value && (offerDetails.value.offerDetails.type === "Stage à la carte" || offerDetails.value.offerDetails.type === "Séjour" ||  offerDetails.value.offerDetails.type === "Stage" ||  offerDetails.value.offerDetails.type === "Stage/Séjour");
          return result;
        });
        const price = computed(() => {
          if (!offerDetails.value || !offerDetails.value.criteria || !formattedDates.value) {
              return 0;
          }
          if (isStageSejour.value) {
            const first = offerDetails.value.criteria[0]
            const unit = parseFloat(first.price) || 0
            return unit * parseInt(quantity.value || "0", 10);
          }

          const selectedDate = convertDateFormat(formattedDates.value);
          const matchingCriteria = offerDetails.value.criteria.find(
              (criterion) => criterion.date === selectedDate
          );

          if (!matchingCriteria) {
              const selectedDateForce = convertDateFormatForce(formattedDates.value);
              const matchingCriteria = offerDetails.value.criteria.find(
                  (criterion) => criterion.date === selectedDateForce
              );
              const priceValue = parseFloat(matchingCriteria.price);
              const quantityValue = parseInt(quantity.value || "0", 10);

              const totalPrice = priceValue * quantityValue;
              return totalPrice;
          }

          const priceValue = parseFloat(matchingCriteria.price);
          const quantityValue = parseInt(quantity.value || "0", 10);

          const totalPrice = priceValue * quantityValue;
          return totalPrice;
        });

  
      
  
      const address = computed(() => {
        const result = `${offerDetails.value.offerDetails.finalAddress} ${offerDetails.value.offerDetails.finalCity} ${offerDetails.value.offerDetails.finalPostcode}`;
        return result;
      });
  
      const mapLink = computed(() => {
        const result = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.value)}`;
        return result;
      });
  
      const presentation = computed(() => {
        const result = offerDetails.value.presentation;
        return result;
      });
  
      const imageSrc = computed(() => {
        const result = offerDetails.value.images[0].replace('187x97/', '').replace('414x219/', '');
        return result;
      });
  
      const schedule = computed(() => {
        const result = findScheduleByDate(formattedDates.value);
        return result;
      });
  
      const findScheduleByDate = (date) => {
        if (!offerDetails.value.criteria) return '';
        
        const criteria = offerDetails.value.criteria.find(criterion => criterion.date === date);
        if (!criteria) return '';
        
        const scheduleCriterion = criteria.criterion.find(c => c.Name === 'Horaire');
        if (!scheduleCriterion) return '';
  
        const [startTime, endTime] = scheduleCriterion.Value.split(' - ');
        const scheduleText = `de ${startTime} à ${endTime}`;
        return scheduleText;
      };
  
      // Ajouter dateSelected pour éviter le warning
      const dateSelected = computed(() => {
        return offerDetails.value?.selected_date || formattedDates.value;
      });

      /* -------------------------------------------------------------
         Écran natif. Le site ne référence aucune de ces valeurs.
         ------------------------------------------------------------- */

      const cfOd = computed(() => offerDetails.value?.offerDetails || {});

      const cfTitre = computed(
        () => cfOd.value.titleOffers || offerDetails.value?.presentation || '',
      );

      /** Le back encode les apostrophes (« l&#039;École ») : on les rend. */
      const cfDecode = (s) => {
        if (!s) return '';
        const zone = document.createElement('textarea');
        zone.innerHTML = String(s);
        return zone.value;
      };

      const cfAdresse = computed(() => cfDecode(address.value));

      /** Le critère retenu (« Confirmé / Galop 3 et + », « 19:00 - 20:30 »). */
      const cfVariation = computed(() => {
        const v = variation.value;
        if (!Array.isArray(v) || !v.length) return '';
        return cfDecode(v.join(' / '));
      });

      /** Ce que le club fournit. Rien n'est inventé : la liste vient de l'offre. */
      const cfInclus = computed(() => {
        const brut = offerDetails.value?.freeEquipments;
        if (!Array.isArray(brut)) return [];
        return brut.map((e) => cfDecode(typeof e === 'string' ? e : e && e.name)).filter(Boolean);
      });

      const cfAssurancePrise = computed(
        () => Boolean(chapkaInsurance.value && chapkaInsurance.value.accepted),
      );

      /**
       * L'assureur peut n'avoir pas encore rendu son contrat au moment du
       * paiement : c'est un avertissement, pas une erreur, la réservation
       * est acquise dans les deux cas.
       */
      const cfAssuranceOk = computed(
        () => Boolean(chapkaSubscribed.value && chapkaSubscribed.value.success),
      );

      const cfTotal = computed(() => {
        let somme = Number(price.value) || 0;
        const eqs = equipmentQuantities.value || {};
        Object.keys(eqs).forEach((k) => {
          const e = eqs[k];
          somme += (Number(e.price) || 0) * (Number(e.quantity) || 0);
        });
        if (cfAssurancePrise.value) somme += Number(chapkaInsurance.value.premium) || 0;
        return somme;
      });

      /** Montants à la française, comme partout ailleurs dans l'application. */
      const cfEuro = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n.toFixed(2).replace('.', ',') : v;
      };

      return {
        cfOd,
        cfTitre,
        cfAdresse,
        cfVariation,
        cfInclus,
        cfAssurancePrise,
        cfAssuranceOk,
        cfTotal,
        cfEuro,
        formattedDates,
        offerDetails,
        quantity,
        equipmentQuantities,
        isStageSejour,
        address,
        mapLink,
        presentation,
        imageSrc,
        schedule,
        findScheduleByDate,
        variation,
        price,
        chapkaInsurance,
        chapkaSubscribed,
        dateSelected,
        apiImg,
        isNative,
      };
    },
  };
  </script>
  
  
<style scoped>
.modal-content {
 background-color: #fff; /* Couleur de fond */
 padding: 20px; /* Espace intérieur */
 border-radius: 8px; /* Bords arrondis */
 box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2); /* Ombre portée */
 width: 80%; /* Largeur du modal */
 max-width: 600px; /* Largeur maximale */
 max-height: 70vh; /* Hauteur maximale */
 overflow-y: auto;
}

.btn-grey {
  background-color: #808080; /* Gris clair */
  color: #d3d3d3; /* Texte gris */
  border: 1px solid #ccc; /* Bordure grise */
}

.close {
 color: #aaa; /* Couleur du bouton de fermeture */
 float: right; /* Positionnement à droite */
 font-size: 28px; /* Taille de la police */
 cursor: pointer; /* Curseur pointeur */
}

.close:hover {
 color: #333; /* Couleur au survol */
}

.modal-content ul {
 list-style: none; /* Suppression des puces */
 padding: 0; /* Suppression du padding */
 margin: 0; /* Suppression de la marge */
 margin-bottom: 20px; 
}

.modal-content li {
 padding: 10px; /* Espace intérieur des éléments de liste */
 border-bottom: 1px solid #eee; /* Bordure inférieure */
 cursor: pointer; /* Curseur pointeur */
}

.modal-content li:hover {
 background-color: #f8f8f8; /* Couleur de fond au survol */
}

/* Styles pour les barres de défilement (optionnel) */
.modal-content::-webkit-scrollbar {
 width: 10px; /* Largeur de la barre de défilement */
}

.modal-content::-webkit-scrollbar-track {
 background: #f1f1f1; /* Couleur de fond de la piste */
}

.modal-content::-webkit-scrollbar-thumb {
 background: #888; /* Couleur de la barre de défilement */
}

.modal-content::-webkit-scrollbar-thumb:hover {
 background: #555; /* Couleur de la barre de défilement au survol */
}

/* Adaptation pour les petits écrans */
@media (max-width: 600px) {
 .modal-content {
     width: 95%; /* Largeur plus grande pour les petits écrans */
     margin: 5% auto; /* Marge verticale réduite */
 }
}

/* Styles pour le bouton "Réserver" */
.reservation-button button {
  color: #fff; /* Couleur du texte par défaut */
  padding: 10px 20px;
  border: none;
  transition: background-color 0.3s ease; /* Transition de la couleur de fond */
}

/* Styles pour le bouton actif (lorsque accepteCGV est vrai) */
.reservation-button button:not(:disabled) {
  cursor: pointer; 
}

/* Styles pour l'animation de transition */
.reservation-button button:hover:not(:disabled) {
  transform: scale(1.05);
  transition: background-color 0.3s ease, transform 0.2s ease; 
}

.button-container-center {
  text-align: center;
}

/* Correctifs d'affichage des champs désactivés sur la page de confirmation */
.reservation-form .form-section.options {
  display: flex !important;
  gap: 10px !important;
  justify-content: center !important;
  align-items: center !important;
  flex-wrap: wrap !important;
}
.reservation-form .btn.quantity-input {
  display: inline-block !important;
  width: auto !important;
  max-width: none !important;
  padding: 8px 12px !important;
  font-size: 0.95rem !important;
  border: 1px solid #ccc !important;
  border-radius: 6px !important;
  background-color: #fff !important;
  height: auto !important;
  line-height: 1.2 !important;
}
.reservation-form .btn.quantity-input[disabled] {
  opacity: 1 !important;
  color: #333 !important;
  background-color: #fff !important;
  -webkit-text-fill-color: #333 !important; /* forcer la couleur sur Safari */
  cursor: default !important;
}

/* Styles pour l'assurance Chapka */
.insurance-confirmation-recap {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(231, 76, 60, 0.08); /* Fond rouge très léger */
  border: 1px solid rgba(231, 76, 60, 0.2);
  border-radius: 12px;
  padding: 12px 20px;
  margin: 15px auto;
  max-width: 600px; /* Largeur max pour centrer proprement */
  width: 100%;
}

.insurance-recap-label {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.shield-icon {
  font-size: 1.4rem;
}

.insurance-name {
  font-family: 'Gagalin', sans-serif;
  color: #c0392b;
  font-size: 1.1rem;
  letter-spacing: 0.5px;
  text-align: left;
}

.insurance-tag {
  background: #27ae60; /* Vert pour confirmé */
  color: white;
  font-size: 0.75rem;
  padding: 4px 10px;
  border-radius: 6px;
  text-transform: uppercase;
  font-weight: bold;
  font-family: 'Dosis', sans-serif;
  letter-spacing: 1px;
  margin-left: 15px;
}

.insurance-price {
  font-family: 'Gagalin', sans-serif;
  color: #c0392b;
  font-size: 1.3rem;
  font-weight: normal;
}

.insurance-confirmation-message {
  margin: 30px auto;
  max-width: 600px;
}

.insurance-success {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 20px;
  background: rgba(39, 174, 96, 0.1);
  border: 1px solid #27ae60;
  border-radius: 15px;
  color: #27ae60;
  text-align: left;
}

.insurance-success i {
  font-size: 2rem;
  color: #27ae60;
  margin-top: 2px;
}

.insurance-success strong {
  display: block;
  margin-bottom: 5px;
  font-size: 1.1rem;
  font-family: 'Gagalin', sans-serif;
}

.insurance-success p {
  margin: 0;
  font-size: 0.95rem;
  opacity: 0.9;
  font-family: 'Dosis', sans-serif;
  font-weight: 500;
}

.insurance-warning {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 20px;
  background: rgba(243, 156, 18, 0.1);
  border: 1px solid #f39c12;
  border-radius: 15px;
  color: #d35400;
  text-align: left;
}

.insurance-warning i {
  font-size: 2rem;
  color: #f39c12;
  margin-top: 2px;
}

.insurance-warning strong {
  display: block;
  margin-bottom: 5px;
  font-size: 1.1rem;
  font-family: 'Gagalin', sans-serif;
}

.insurance-warning p {
  margin: 0;
  font-size: 0.95rem;
  opacity: 0.9;
  font-family: 'Dosis', sans-serif;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 500px) {
  .insurance-tag {
    display: none;
  }
  .insurance-name {
    font-size: 0.9rem;
  }
  .insurance-confirmation-recap {
    padding: 10px 15px;
  }
}

</style>

<style scoped>
/* =========================================================================
   STYLES NATIFS iOS / ANDROID — ConfirmationPage.vue
   Préfixés par body.is-native pour ne JAMAIS impacter le site web.

   Intention : cet écran n'est plus une page de site, c'est un écran de
   réussite. Une coche verte, puis deux cartes blanches posées sur le fond
   chaud (le détail de l'activité, le récapitulatif), puis une seule action.
   ========================================================================= */

/* --- Bandeau de sport, réduit : il illustre, il ne domine plus --- */
body.is-native .hero3 {
  height: 132px !important;
  min-height: 132px !important;
  background-size: cover !important;
  background-position: center !important;
}

@supports (padding-top: env(safe-area-inset-top)) {
  body.is-native .hero3 {
    padding-top: var(--oc-safe-top);
    height: calc(132px + var(--oc-safe-top)) !important;
  }
}

/* --- Conteneur : une pile d'îlots sur le fond chaud --- */
body.is-native .activity-details {
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: var(--oc-sp-5) var(--oc-sp-4) var(--oc-sp-8);
  background: var(--oc-bg);
}

/* --- Bloc de réussite (rendu uniquement en natif) --- */
body.is-native .native-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0 var(--oc-sp-2) var(--oc-sp-6);
}

body.is-native .native-success__mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 84px;
  height: 84px;
  margin-bottom: var(--oc-sp-4);
  color: var(--oc-success);
  background: var(--oc-success-bg);
  border-radius: var(--oc-r-full);
  box-shadow: 0 0 0 6px var(--oc-surface);
}

body.is-native .native-success__icon {
  width: 46px;
  height: 46px;
  display: block;
}

body.is-native .native-success__title {
  margin: 0 0 var(--oc-sp-2);
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-display);
  font-weight: 400;
  line-height: 1.15;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: var(--oc-ink);
}

body.is-native .native-success__text {
  margin: 0;
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-base);
  color: var(--oc-ink-2);
}

/* Le remerciement est déjà porté par le bloc de réussite en haut d'écran. */
body.is-native .thank-you {
  display: none;
}

/* --- Carte 1 : l'activité réservée --- */
body.is-native .activity-details-top {
  display: block !important;
  grid-template-columns: none !important;
  margin: 0 0 var(--oc-sp-4);
  padding: var(--oc-sp-4);
  background: var(--oc-surface);
  border-radius: var(--oc-r-lg);
  box-shadow: var(--oc-e1);
}

body.is-native .activity-details-top h2 {
  margin: 0 0 var(--oc-sp-2);
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-title);
  font-weight: 400;
  line-height: 1.2;
  color: var(--oc-ink);
  text-align: left !important;
}

body.is-native .activity-details-top h2:last-child {
  margin-bottom: 0;
  font-size: var(--oc-text-base);
  color: var(--oc-ink-2);
}

body.is-native .activity-details-top h3 {
  margin: 0 0 var(--oc-sp-3);
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-base);
  font-weight: 600;
  line-height: 1.45;
  color: var(--oc-ink-2);
  text-align: left;
}

/* --- Carte 2 : lieu, horaire, visuel --- */
body.is-native .activity-details-description {
  display: block !important;
  grid-template-columns: none !important;
  width: 100%;
  margin: 0 0 var(--oc-sp-4);
  padding: var(--oc-sp-4);
  background: var(--oc-surface);
  border-radius: var(--oc-r-lg);
  box-shadow: var(--oc-e1);
}

body.is-native .activity-details-description .activity-location {
  margin: 0;
  font-size: var(--oc-text-base);
  line-height: 1.5;
  color: var(--oc-ink-2);
  text-align: left;
}

body.is-native .activity-details-description .activity-location a {
  display: inline-block;
  min-height: 34px;
  padding: var(--oc-sp-1) 0;
  font-weight: 700;
  color: var(--oc-brand);
}

body.is-native .activity-details-description .image_activity {
  width: 100%;
  margin: var(--oc-sp-4) 0 0;
}

body.is-native .activity-details-description .image_activity img {
  display: block;
  width: 100% !important;
  height: auto !important;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  border-radius: var(--oc-r-md);
  background: var(--oc-surface-2);
}

/* --- Carte 3 : le récapitulatif chiffré --- */
body.is-native .reservation-form {
  display: block !important;
  width: 100%;
  padding: var(--oc-sp-4);
  text-align: left;
  background: var(--oc-surface);
  border-radius: var(--oc-r-lg);
  box-shadow: var(--oc-e1);
}

body.is-native .reservation-form h3 {
  margin: 0 0 var(--oc-sp-2);
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-title);
  font-weight: 400;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: var(--oc-ink);
  text-align: left;
}

/* Chaque ligne du récapitulatif : libellé à gauche, quantité, prix à droite.
   Les champs désactivés sont remis à plat : ce sont des valeurs, pas des
   contrôles ; les encadrer les faisait passer pour des champs modifiables. */
body.is-native .reservation-form .form-section.options {
  display: flex !important;
  flex-wrap: nowrap !important;
  align-items: center;
  justify-content: space-between !important;
  gap: var(--oc-sp-3) !important;
  width: 100%;
  padding: var(--oc-sp-3) 0;
  border-bottom: 1px solid var(--oc-line);
}

body.is-native .reservation-form .form-section.options:last-of-type {
  border-bottom: 0;
}

body.is-native .reservation-form .btn.quantity-input {
  flex: 0 0 auto;
  width: auto !important;
  min-width: 0;
  min-height: 0 !important;
  height: auto !important;
  padding: 0 !important;
  font-family: var(--oc-font-text) !important;
  font-size: var(--oc-text-base) !important;
  font-weight: 600 !important;
  line-height: 1.3 !important;
  text-align: left;
  color: var(--oc-ink) !important;
  -webkit-text-fill-color: var(--oc-ink) !important;
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  opacity: 1 !important;
}

body.is-native .reservation-form .form-section.options .btn.quantity-input:first-child {
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

body.is-native .reservation-form .form-section.options .btn.quantity-input[type="number"] {
  flex: 0 0 40px;
  text-align: center;
  color: var(--oc-ink-2) !important;
  -webkit-text-fill-color: var(--oc-ink-2) !important;
}

body.is-native .reservation-form .form-section.options .btn.quantity-input:last-child {
  text-align: right;
  font-family: var(--oc-font-display) !important;
  font-size: var(--oc-text-lg) !important;
  font-weight: 400 !important;
  color: var(--oc-brand) !important;
  -webkit-text-fill-color: var(--oc-brand) !important;
}

/* --- Récapitulatif d'assurance --- */
body.is-native .insurance-confirmation-recap {
  max-width: 100% !important;
  width: 100%;
  margin: var(--oc-sp-4) 0 0 !important;
  padding: var(--oc-sp-3) var(--oc-sp-4) !important;
  background: var(--oc-brand-50);
  border: 0;
  border-radius: var(--oc-r-md);
}

body.is-native .insurance-confirmation-recap .insurance-recap-label {
  gap: var(--oc-sp-2);
  min-width: 0;
}

body.is-native .insurance-confirmation-recap .insurance-name {
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-base);
  color: var(--oc-ink);
}

body.is-native .insurance-confirmation-recap .insurance-tag {
  margin-left: 0;
  padding: 3px var(--oc-sp-2);
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-xs);
  font-weight: 700;
  letter-spacing: 0.3px;
  color: var(--oc-success);
  background: var(--oc-success-bg);
  border-radius: var(--oc-r-sm);
}

body.is-native .insurance-confirmation-recap .insurance-price {
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-lg);
  color: var(--oc-brand);
}

/* --- Messages d'état de la souscription --- */
body.is-native .insurance-confirmation-message {
  max-width: 100% !important;
  margin: var(--oc-sp-4) 0 0 !important;
}

body.is-native .insurance-success,
body.is-native .insurance-warning {
  gap: var(--oc-sp-3);
  padding: var(--oc-sp-4) !important;
  border: 0;
  border-radius: var(--oc-r-lg);
  color: var(--oc-ink);
}

body.is-native .insurance-success {
  background: var(--oc-success-bg);
}

body.is-native .insurance-warning {
  background: var(--oc-warning-bg);
}

body.is-native .insurance-success i {
  margin-top: 0;
  font-size: 1.5rem;
  color: var(--oc-success);
}

body.is-native .insurance-warning i {
  margin-top: 0;
  font-size: 1.5rem;
  color: var(--oc-warning);
}

body.is-native .insurance-success strong,
body.is-native .insurance-warning strong {
  margin-bottom: var(--oc-sp-1);
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-base);
  font-weight: 400;
  letter-spacing: 0.3px;
  color: var(--oc-ink);
}

body.is-native .insurance-success p,
body.is-native .insurance-warning p {
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-sm);
  line-height: 1.45;
  opacity: 1;
  color: var(--oc-ink-2);
}

/* --- Action unique de sortie --- */
body.is-native .button-container-center {
  padding: var(--oc-sp-6) 0 0;
  text-align: center;
}

/* Le fond est posé en style en ligne dans le gabarit : il faut !important
   pour ramener le bouton sur le rouge de marque unique. */
body.is-native .login-button {
  display: flex;
  width: 100%;
  max-width: 100%;
  min-height: var(--oc-tap);
  padding: 0 var(--oc-sp-5);
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-base);
  font-weight: 700;
  letter-spacing: 0.2px;
  align-items: center;
  justify-content: center;
  color: var(--oc-brand-ink) !important;
  background: var(--oc-brand) !important;
  background-color: var(--oc-brand) !important;
  border: 0;
  border-radius: var(--oc-r-full);
  box-shadow: var(--oc-e1);
  text-decoration: none;
  box-sizing: border-box;
}

body.is-native .login-button:active {
  background: var(--oc-brand-600) !important;
  background-color: var(--oc-brand-600) !important;
}

/* =========================================================================
   CONFIRMATION — rendu de l'application mobile (préfixe cf-)
   Toutes ces règles sont portées par body.is-native et n'habillent qu'un
   balisage rendu sous `v-if="isNative"`. Le site est intact.
   ========================================================================= */

/* Remise à zéro au niveau des éléments : style.css peint les balises nues.
   `svg` en est exclu — `all: unset` écrase ses attributs de présentation. */
body.is-native .cf-page header,
body.is-native .cf-page section,
body.is-native .cf-page h1,
body.is-native .cf-page p,
body.is-native .cf-page ol,
body.is-native .cf-page li,
body.is-native .cf-page span,
body.is-native .cf-page div,
body.is-native .cf-page a,
body.is-native .cf-page img {
  all: unset;
  display: revert;
  /* `all: unset` retire le `overflow: clip` que le navigateur applique
     aux éléments remplacés : sans lui, le débordement d'`object-fit:
     cover` se peint HORS du cadre de l'image. */
  overflow: clip;
  box-sizing: border-box;
}

body.is-native .cf-page {
  --cf-bg: #F5F0E6;
  --cf-surface: #EFE8DA;
  --cf-raised: #E5DDD0;
  --cf-dark: #2B231F;
  --cf-ink: #28201C;
  --cf-ink-2: #6B5E54;
  --cf-ink-3: #8C7E73;
  --cf-on-dark: #FAF6EE;
  --cf-red: #DD4124;
  --cf-line: rgba(107, 94, 84, 0.15);
  --cf-ok: #1E8E5A;
  --cf-ok-bg: #E7F5EE;
  --cf-ok-line: #A8D9C0;
  --cf-wait: #B87400;
  --cf-wait-bg: #FDF3E2;
  --cf-wait-line: #E9CFA0;

  display: block;
  box-sizing: border-box;
  background: var(--cf-bg);
  font-family: var(--oc-font-text);
  color: var(--cf-ink);
  padding: 16px 14px calc(var(--oc-tabbar-h) + var(--oc-safe-bottom) + 24px) 14px;
}

/* ---------- Confirmation ---------- */

body.is-native .cf-page .cf-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 6px;
  padding: 6px 8px 18px 8px;
}

body.is-native .cf-page .cf-hero__mark {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 68px;
  height: 68px;
  margin-bottom: 4px;
  border-radius: 50%;
  background: var(--cf-ok-bg);
  border: 2px solid var(--cf-ok-line);
  color: var(--cf-ok);
}

/* Le tracé se dessine à l'apparition : pas de rebond, pas de boucle. */
body.is-native .cf-page .cf-hero__check {
  width: 36px;
  height: 36px;
  stroke-width: 3.4;
  stroke-dasharray: 32;
  stroke-dashoffset: 32;
  animation: cf-trace 620ms cubic-bezier(0.2, 0.8, 0.2, 1) 140ms forwards;
}

body.is-native .cf-page .cf-hero__title {
  font-family: var(--oc-font-display);
  font-size: 26px;
  line-height: 1.06;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

body.is-native .cf-page .cf-hero__sub {
  max-width: 310px;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
  color: var(--cf-ink-2);
}

/* ---------- Cartes ---------- */

body.is-native .cf-page .cf-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  margin-bottom: 14px;
  border-radius: 18px;
  background: var(--cf-surface);
  border: 1px solid var(--cf-line);
}

body.is-native .cf-page .cf-card__label {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

/* ---------- Date : l'information qu'on revient chercher ---------- */

body.is-native .cf-page .cf-date {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 12px 14px;
  border-radius: 14px;
  background: var(--cf-dark);
  color: var(--cf-on-dark);
}

body.is-native .cf-page .cf-date__label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--cf-red);
}

body.is-native .cf-page .cf-date__val {
  font-family: var(--oc-font-display);
  font-size: 20px;
  line-height: 1.15;
}

/* ---------- Activité ---------- */

body.is-native .cf-page .cf-act {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Jamais en pleine largeur : une partie du catalogue n'est servie qu'en
   388 × 200 et devient floue au-delà. */
body.is-native .cf-page .cf-act__img {
  flex: 0 0 auto;
  width: 80px;
  height: 60px;
  object-fit: cover;
  border-radius: 10px;
  background: var(--cf-raised);
}

body.is-native .cf-page .cf-act__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

body.is-native .cf-page .cf-act__sport {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--cf-red);
}

body.is-native .cf-page .cf-act__title {
  font-size: 15px;
  font-weight: 800;
  line-height: 1.2;
}

body.is-native .cf-page .cf-act__who {
  font-size: 13px;
  font-weight: 600;
  color: var(--cf-ink-2);
}

/* ---------- Adresse ---------- */

body.is-native .cf-page .cf-map {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 56px;
  padding: 10px 12px;
  border-radius: 12px;
  background: var(--cf-bg);
  border: 1.5px solid rgba(107, 94, 84, 0.18);
  cursor: pointer;
  transition: border-color 180ms ease;
}

body.is-native .cf-page .cf-map:active { border-color: var(--cf-red); }

body.is-native .cf-page .cf-map__pin {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(221, 65, 36, 0.12);
  color: var(--cf-red);
}

body.is-native .cf-page .cf-map__text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1 1 auto;
  min-width: 0;
}

body.is-native .cf-page .cf-map__name {
  font-size: 14px;
  font-weight: 800;
}

body.is-native .cf-page .cf-map__addr {
  font-size: 12px;
  font-weight: 600;
  color: var(--cf-ink-2);
  line-height: 1.3;
}

body.is-native .cf-page .cf-map__go {
  flex: 0 0 auto;
  color: var(--cf-ink-3);
}

/* ---------- Détail réglé ---------- */

body.is-native .cf-page .cf-recap {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

body.is-native .cf-page .cf-recap__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 13px;
  font-weight: 600;
  color: var(--cf-ink-2);
}

body.is-native .cf-page .cf-recap__row--total {
  padding-top: 8px;
  border-top: 1px dashed rgba(107, 94, 84, 0.25);
  font-size: 16px;
  font-weight: 800;
  color: var(--cf-ink);
  font-variant-numeric: tabular-nums;
}

body.is-native .cf-page .cf-chip {
  display: inline-block;
  margin-left: 4px;
  padding: 2px 6px;
  border-radius: 5px;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  color: var(--cf-ok);
  background: var(--cf-ok-bg);
}

/* ---------- Assurance ---------- */

body.is-native .cf-page .cf-ins {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  border-radius: 12px;
  border: 1.5px solid transparent;
}

body.is-native .cf-page .cf-ins.is-ok {
  background: var(--cf-ok-bg);
  border-color: var(--cf-ok-line);
  color: var(--cf-ok);
}

body.is-native .cf-page .cf-ins.is-wait {
  background: var(--cf-wait-bg);
  border-color: var(--cf-wait-line);
  color: var(--cf-wait);
}

body.is-native .cf-page .cf-ins__icon {
  flex: 0 0 auto;
  display: flex;
  margin-top: 1px;
}

body.is-native .cf-page .cf-ins__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

body.is-native .cf-page .cf-ins__title {
  font-size: 14px;
  font-weight: 800;
}

body.is-native .cf-page .cf-ins__desc {
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.35;
}

/* ---------- Et maintenant ---------- */

body.is-native .cf-page .cf-next {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

body.is-native .cf-page .cf-next__item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: var(--cf-bg);
  border: 1px solid rgba(107, 94, 84, 0.12);
  list-style: none;
}

body.is-native .cf-page .cf-next__num {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  margin-top: 1px;
  border-radius: 50%;
  background: var(--cf-dark);
  color: var(--cf-on-dark);
  font-family: var(--oc-font-display);
  font-size: 13px;
  line-height: 1;
}

body.is-native .cf-page .cf-next__text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

body.is-native .cf-page .cf-next__head {
  font-size: 14px;
  font-weight: 800;
}

body.is-native .cf-page .cf-next__sub {
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.35;
  color: var(--cf-ink-2);
}

body.is-native .cf-page .cf-link {
  color: var(--cf-red);
  text-decoration: underline;
  cursor: pointer;
  padding: 10px 0;
  margin: -10px 0;
}

/* ---------- Sorties ---------- */

body.is-native .cf-page .cf-exit {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 4px;
}

body.is-native .cf-page .cf-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 50px;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
  text-align: center;
  transition: transform 180ms ease, background-color 180ms ease;
}

body.is-native .cf-page .cf-btn--main {
  background: var(--cf-red);
  color: var(--cf-on-dark);
  box-shadow: 0 4px 14px rgba(221, 65, 36, 0.32);
}

body.is-native .cf-page .cf-btn--ghost {
  background: transparent;
  border: 1.5px solid rgba(107, 94, 84, 0.25);
  color: var(--cf-ink);
  font-size: 15px;
  min-height: 48px;
}

body.is-native .cf-page .cf-btn:active { transform: scale(0.98); }

@keyframes cf-trace { to { stroke-dashoffset: 0; } }

@media (prefers-reduced-motion: reduce) {
  body.is-native .cf-page .cf-hero__check {
    animation: none;
    stroke-dashoffset: 0;
  }
}
</style>
